import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import directionsData from "../data/private/directions.json";
import { logActivity } from "../utils/activity";
import normalizeApiError from "../utils/normalizeApiError";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import { getDirections, updateDirections } from "../api/directions.api";
import { setDebugLastAction } from "../utils/debugState";

export default function DashboardDirections() {
  const navigate = useNavigate();
  const selectedClientId = localStorage.getItem("myv_selected_client_id") || "";
  const [rotation, setRotation] = useState(0);
  const [hasResult, setHasResult] = useState(false);
  const [result, setResult] = useState(null);
  const [missingPayload, setMissingPayload] = useState(false);
  const [domainBlocked, setDomainBlocked] = useState(false);
  const [lastUpdateId, setLastUpdateId] = useState("");
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [notReady, setNotReady] = useState(false);
  const dragRef = useRef({ active: false, startX: 0, startRotation: 0 });
  const selectedPropertyId =
    localStorage.getItem("myv_selected_property_id") || "";

  const extractDirectionsResult = (payload) => {
    if (!payload || typeof payload !== "object") {
      return { hasData: false };
    }
    const nextRotation =
      typeof payload.rotation === "number" ? payload.rotation : null;
    const overallScore =
      payload.overallScore ?? payload.overall_score ?? payload.score ?? null;
    const rating = payload.rating ?? null;
    const directions = payload.directions || null;
    const hasDirections =
      directions &&
      ["north", "east", "south", "west"].some(
        (key) => directions[key] !== undefined && directions[key] !== null
      );
    const hasScore = overallScore !== null || rating !== null;
    const hasRotation = nextRotation !== null;
    return {
      rotation: nextRotation,
      overallScore,
      rating,
      directions: hasDirections ? directions : null,
      hasData: hasDirections || hasScore || hasRotation,
    };
  };

  const isDomainBlockingError = (error) => {
    const code = error?.response?.data?.code;
    return code === "INVALID_ANALYSIS_ID";
  };

  if (!selectedClientId) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="No client selected"
          message="Select a client to continue."
          testId="dashboard-empty-directions-client"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/clients")}>
          Select Client
        </button>
      </div>
    );
  }

  if (!selectedPropertyId) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="No property selected"
          message="Select a property to continue."
          testId="dashboard-empty-directions-property"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/properties")}>
          Select Property
        </button>
      </div>
    );
  }

  if (domainBlocked) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="Directions unavailable"
          message="The analysis context could not be resolved for this property."
          testId="dashboard-empty-directions-domain"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/properties")}>
          Select Property
        </button>
      </div>
    );
  }

  const handleRotate = (delta) => {
    setRotation((prev) => prev + delta);
    logActivity("directions_rotate", { delta });
  };

  const handleUpdate = async () => {
    if (notReady || domainBlocked) {
      return;
    }
    setUpdateLoading(true);
    setLoading(true);
    setMissingPayload(false);
    setDomainBlocked(false);
    try {
      const payload = await getDirections({
        propertyId: selectedPropertyId || undefined,
        rotation,
      });
      const extracted = extractDirectionsResult(payload);
      if (!extracted.hasData) {
        setHasResult(false);
        setResult(null);
        setMissingPayload(true);
        setLoadError(null);
        return;
      }
      setHasResult(true);
      setResult(extracted);
      if (typeof extracted.rotation === "number") {
        setRotation(extracted.rotation);
      }
      const response = await updateDirections({
        propertyId: selectedPropertyId || undefined,
        rotation: extracted.rotation ?? rotation,
      });
      setLastUpdateId(response?.id || "");
      logActivity("directions_updated", { rotation });
      logActivity("directions_loaded", { propertyId: selectedPropertyId || "" });
      setDebugLastAction("Update directions");
      setLoadError(null);
    } catch (error) {
      const code = error?.response?.data?.code;
      if (code === "BILLING_CREDITS_EXHAUSTED") {
        navigate("/billing");
        setUpdateLoading(false);
        return;
      }
      if (isDomainBlockingError(error)) {
        setHasResult(false);
        setResult(null);
        setDomainBlocked(true);
        setLoadError(null);
        return;
      }
      if (error?.response?.status === 501) {
        setNotReady(true);
        setHasResult(false);
        setLoadError(null);
        return;
      }
      setHasResult(false);
      setLoadError(normalizeApiError(error));
      logActivity("api_error", { module: "directions" });
    } finally {
      setUpdateLoading(false);
      setLoading(false);
    }
  };

  const handleDragStart = (event) => {
    dragRef.current = {
      active: true,
      startX: event.clientX,
      startRotation: rotation,
    };
  };

  const handleDragMove = (event) => {
    if (!dragRef.current.active) return;
    const delta = event.clientX - dragRef.current.startX;
    setRotation(dragRef.current.startRotation + delta);
  };

  const handleDragEnd = () => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    logActivity("directions_updated", { rotation });
    handleUpdate();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          {directionsData.title}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {directionsData.subtitle}
        </p>
      </div>

      {notReady && (
        <div className="card p-4">
          <p className="text-sm font-semibold text-slate-900">
            Directions coming soon
          </p>
          <p className="mt-1 text-sm text-slate-600">
            This tool is not available yet.
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-[1fr_0.7fr]">
        <div className="card card-hover p-6">
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>N</span>
            <span>E</span>
            <span>S</span>
            <span>W</span>
          </div>
          <div className="mt-4 flex items-center justify-center">
            {loading ? (
              <div className="h-48 w-48 animate-pulse rounded-full border border-slate-200 bg-slate-100" />
            ) : (
              <div
                className="flex h-48 w-48 items-center justify-center rounded-full border border-slate-200 transition-transform duration-200"
                style={{ transform: `rotate(${rotation}deg)` }}
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
              >
                <div className="h-24 w-1 rounded bg-amber-500" />
              </div>
            )}
          </div>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <button
              className="btn btn-secondary"
              onClick={() => handleRotate(-15)}
            >
              {directionsData.controls.rotateLeft}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleRotate(15)}
            >
              {directionsData.controls.rotateRight}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                setRotation(0);
                logActivity("directions_reset");
                handleUpdate();
              }}
            >
              {directionsData.controls.reset}
            </button>
          </div>
          <label className="mt-6 block text-xs text-slate-500">
            {directionsData.controls.sliderLabel}
          </label>
          <input
            className="mt-2 w-full"
            type="range"
            min="-180"
            max="180"
            value={rotation}
            onChange={(event) => {
              setRotation(Number(event.target.value));
              logActivity("directions_slider", { value: event.target.value });
            }}
            onMouseUp={handleUpdate}
          />
          <button
            className="btn btn-secondary mt-4 w-full"
            onClick={handleUpdate}
            disabled={updateLoading || notReady}
          >
            {updateLoading
              ? directionsData.actions.updatingLabel
              : directionsData.actions.updateLabel}
          </button>
        </div>

        <div className="card card-hover p-6">
          <h2 className="text-base font-semibold text-slate-900">
            {directionsData.info.title}
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            {directionsData.info.description}
          </p>
          {loading ? (
            <div className="mt-4 space-y-3">
              <div className="h-3 w-full animate-pulse rounded bg-slate-100" />
              <div className="h-3 w-5/6 animate-pulse rounded bg-slate-100" />
              <div className="h-3 w-4/6 animate-pulse rounded bg-slate-100" />
            </div>
          ) : hasResult ? (
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              {result?.directions && (
                <div className="grid gap-2 text-xs text-slate-600">
                  <div>North: {result.directions.north ?? "—"}</div>
                  <div>East: {result.directions.east ?? "—"}</div>
                  <div>South: {result.directions.south ?? "—"}</div>
                  <div>West: {result.directions.west ?? "—"}</div>
                </div>
              )}
              {(result?.overallScore !== null ||
                result?.rating !== null ||
                typeof result?.rotation === "number") && (
                <div className="grid gap-2 text-xs text-slate-600">
                  {typeof result?.rotation === "number" && (
                    <div>Rotation: {result.rotation}</div>
                  )}
                  {result?.overallScore !== null && (
                    <div>Overall score: {result.overallScore}</div>
                  )}
                  {result?.rating !== null && <div>Rating: {result.rating}</div>}
                </div>
              )}
              {lastUpdateId && (
                <div className="text-xs text-slate-500">
                  Update ID: {lastUpdateId}
                </div>
              )}
            </div>
          ) : missingPayload ? (
            <div className="mt-4">
              <EmptyState
                title="Awaiting backend payload contract"
                message="Directions response did not include usable fields."
                testId="dashboard-empty-directions-payload"
              />
            </div>
          ) : (
            <div className="mt-4">
              <EmptyState
                title="No directions yet"
                message="Run an analysis to see directions here."
                testId="dashboard-empty-directions"
              />
            </div>
          )}
        </div>
      </div>

      {loadError && <ErrorState error={loadError} />}

    </div>
  );
}
